import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { type ClinicalHistoryStatus, reportsApi } from '@/api/reports';

const isClinicalHistoryProcessing = (status?: ClinicalHistoryStatus, ready?: boolean) =>
    Boolean(!ready && (status === 'queued' || status === 'building'));

interface UseClinicalHistoryStatusOptions {
    enabled?: boolean;
    pollInterval?: number;
}

export function useClinicalHistoryStatus(
    reportId?: string,
    options?: UseClinicalHistoryStatusOptions,
) {
    const { enabled = true, pollInterval = 4000 } = options ?? {};

    const query = useQuery({
        queryKey: ['reports', reportId, 'clinical-history-status'],
        queryFn: () => reportsApi.getClinicalHistoryStatus(reportId as string),
        enabled: enabled && !!reportId,
        staleTime: 0,
    });

    const { data, refetch } = query;

    useEffect(() => {
        if (!enabled || !reportId) return;

        const status = data?.data.clinical_history.status;
        const ready = data?.data.clinical_history.ready;

        if (!isClinicalHistoryProcessing(status, ready)) return;

        const timer = window.setInterval(() => {
            void refetch();
        }, pollInterval);

        return () => {
            window.clearInterval(timer);
        };
    }, [
        enabled,
        reportId,
        data?.data.clinical_history.status,
        data?.data.clinical_history.ready,
        refetch,
        pollInterval,
    ]);

    return query;
}
